import cloneDeep from 'lodash/cloneDeep';
import { call, select } from 'redux-saga/effects';

import {
    KEY_META_FREQUENCY,
    KEY_META_KMPLAN,
    KEY_META_TERM
} from '../../constants/payment';
import {
    getFinanceOptions,
    getMetaFinancePlans,
    getPaymentOptions
} from '../selectors';
import updatePaymentOptions from './api/updatePaymentOptions';

/**
 * Try to find if the current value is still relevant with the new finance plan.
 *  - If can't find anything in metadata, return null.
 *  - If can't find the same value, return the first value in the list.
 *  - If found the same value, keep it.
 *
 * @param {Object} financePlan - Found in meta data, describe the type of financePlan
 * @param {String} propertyName - Name of the property where to search
 * @param {Number} value - The current value
 * @returns {Number|null}
 */
const findMatchingValueInFinancePlan = (financePlan, propertyName, value) => {
    const valueList = financePlan[propertyName];

    if (!Array.isArray(valueList) || valueList.length === 0) {
        return null;
    }

    return valueList.indexOf(value) !== -1 ? value : valueList[0];
};

/**
 * @param {Object} payload
 */
function* changePlan(payload) {
    const {
        financePlan: newFinancePlan,
        purchaseMethod: newPurchaseMethod,
        isSelected,
        index
    } = payload;

    // Get the metadata of the new finance plan
    const metaFinancePlans = yield select(getMetaFinancePlans);
    const newFinancePlanMeta = metaFinancePlans.find(item => newFinancePlan === item.financePlan
        && newPurchaseMethod === item.purchaseMethod);

    // The current card where the change is coming from
    const financeOptions = yield select(getFinanceOptions);
    const currentFinanceOptions = financeOptions[index];
    const {
        paymentFrequency,
        kmPerYearPlan,
        selectedCashDown,
        selectedTerm
    } = currentFinanceOptions;

    // Try to detect the best values we can with the new finance plan
    const newKmPlan = findMatchingValueInFinancePlan(newFinancePlanMeta, KEY_META_KMPLAN, kmPerYearPlan);
    const newPaymentFrequency = findMatchingValueInFinancePlan(newFinancePlanMeta, KEY_META_FREQUENCY, paymentFrequency);
    const newTerm = findMatchingValueInFinancePlan(newFinancePlanMeta, KEY_META_TERM, selectedTerm);

    // New data to send
    const newOptions = {
        financePlan: newFinancePlan,
        purchaseMethod: newPurchaseMethod,
        paymentFrequency: newPaymentFrequency,
        kmPerYearPlan: newKmPlan,
        selectedTerm: newTerm
    };

    // Deep copy && Update
    const transformedFinanceOptions = cloneDeep(financeOptions);
    Object.assign(transformedFinanceOptions[index], newOptions);

    // Build body request
    const paymentOptions = yield select(getPaymentOptions);
    const body = {
        ...paymentOptions,
        financeOptions: transformedFinanceOptions
    };

    // If the card is selected, send new values at the root of the body also
    if (isSelected) {
        Object.assign(body, {
            financePlan: newFinancePlan,
            purchaseMethod: newPurchaseMethod,
            paymentFrequency: newPaymentFrequency,
            kmPerYearPlan: newKmPlan,
            term: newTerm,
            cashDown: selectedCashDown
        });
    }

    yield call(updatePaymentOptions, body);
}

export default changePlan;
